<template>
  <div class="p-4 relative z-[]">
    <div class="flex items-center pl-0 py-2 pr-4 px-4 gap-4 relative">
      <!-- Search Input Container -->
      <div
        class="pl-12 md:pl-4 pr-8 rounded-full absolute w-full h-full left-0 top-0 flex items-center bg-slate-200 px-6 transition-all"
        ref="searchContainer"
      >
        <svg class="w-5 h-5 text-gray-500 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          v-model="searchQuery"
          type="search"
          placeholder="Rechercher sur Rapidoo Admin (menus, clients, livreurs, commandes...)"
          class="w-full h-full bg-transparent focus:!outline-none focus:!ring-0 focus:!border-none text-sm font-medium text-gray-700 border-none outline-none"
          @focus="onSearchFocus"
          @input="onSearchInput"
        />
        <button
          v-if="searchQuery"
          @click="clearSearch"
          class="text-gray-400 hover:text-gray-600 transition ml-2"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Search Results Floating Panel -->
      <div
        v-if="showResults && (filteredMenus.length > 0 || hasDataResults)"
        class="absolute left-0 right-0 top-full mt-2 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border border-gray-100 max-h-[70vh] overflow-y-auto z-[999] p-4 space-y-4"
      >
        <!-- Category: Menus -->
        <div v-if="filteredMenus.length > 0">
          <div class="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-2 px-2">Menus & Pages</div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
            <div
              v-for="menu in filteredMenus"
              :key="menu.name"
              @click="goToMenu(menu)"
              class="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-100/80 transition-colors cursor-pointer text-sm font-semibold text-gray-700"
            >
              <div class="w-7 h-7 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
              <span>{{ menu.text }}</span>
            </div>
          </div>
        </div>

        <!-- Category: Customers -->
        <div v-if="dataResults.customers.length > 0">
          <div class="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-2 px-2">Clients</div>
          <div class="space-y-1">
            <div
              v-for="c in dataResults.customers"
              :key="c.id || c.phoneNumber"
              @click="copyAndGo(c, 'customers')"
              class="flex justify-between items-center p-2.5 rounded-xl hover:bg-slate-100/80 transition-colors cursor-pointer text-sm"
            >
              <div class="flex flex-col">
                <span class="font-bold text-gray-800">{{ c.name || 'Nom Inconnu' }}</span>
                <span class="text-xs text-gray-500">{{ c.phoneNumber || c.email || 'Pas de contact' }}</span>
              </div>
              <span class="text-[10px] bg-slate-100 text-slate-500 font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">Copier ID & Aller</span>
            </div>
          </div>
        </div>

        <!-- Category: Riders -->
        <div v-if="dataResults.riders.length > 0">
          <div class="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-2 px-2">Livreurs</div>
          <div class="space-y-1">
            <div
              v-for="r in dataResults.riders"
              :key="r.id || r.phoneNumber"
              @click="copyAndGo(r, 'riders')"
              class="flex justify-between items-center p-2.5 rounded-xl hover:bg-slate-100/80 transition-colors cursor-pointer text-sm"
            >
              <div class="flex flex-col">
                <span class="font-bold text-gray-800">{{ r.name || r.firstName + ' ' + r.lastName }}</span>
                <span class="text-xs text-gray-500">{{ r.phoneNumber || r.email || 'Pas de contact' }}</span>
              </div>
              <span class="text-[10px] bg-slate-100 text-slate-500 font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">Copier ID & Aller</span>
            </div>
          </div>
        </div>

        <!-- Category: Restaurants -->
        <div v-if="dataResults.restaurants.length > 0">
          <div class="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-2 px-2">Restaurants</div>
          <div class="space-y-1">
            <div
              v-for="rest in dataResults.restaurants"
              :key="rest.id || rest.name"
              @click="copyAndGo(rest, 'restaurants')"
              class="flex justify-between items-center p-2.5 rounded-xl hover:bg-slate-100/80 transition-colors cursor-pointer text-sm"
            >
              <div class="flex flex-col">
                <span class="font-bold text-gray-800">{{ rest.name }}</span>
                <span class="text-xs text-gray-500">{{ rest.address || rest.phoneNumber || 'Pas d\'adresse' }}</span>
              </div>
              <span class="text-[10px] bg-slate-100 text-slate-500 font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">Copier ID & Aller</span>
            </div>
          </div>
        </div>

        <!-- Category: Orders -->
        <div v-if="dataResults.orders.length > 0">
          <div class="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-2 px-2">Commandes</div>
          <div class="space-y-1">
            <div
              v-for="order in dataResults.orders"
              :key="order.id"
              @click="copyAndGo(order, 'orders')"
              class="flex justify-between items-center p-2.5 rounded-xl hover:bg-slate-100/80 transition-colors cursor-pointer text-sm"
            >
              <div class="flex flex-col">
                <span class="font-bold text-gray-800">Commande #{{ order.id }}</span>
                <span class="text-xs text-gray-500">{{ order.customerName || 'Client' }} ➔ {{ order.restaurantName || 'Resto' }}</span>
              </div>
              <span class="text-[10px] bg-slate-100 text-slate-500 font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">Copier ID & Aller</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Profile Section -->
      <div class="flex gap-2 left-0 items-center absolute md:hidden ml-auto">
        <div
          class="w-12 h-12 p-3 cursor-pointer"
          @click="Events.dispatch(CUSTOM_EVENTS.TOGGLE_MENU)"
          id="pannelHamerger"
        >
          <img src="./assets/images/menu.svg" alt="" class="w-full h-full" />
        </div>
      </div>
      <router-link
        :to="{ name: 'profile' }"
        :class="{ 'ml-auto': !isMd }"
        class="flex gap-2 items-center text-right relative ml-auto"
      >
        <div class="w-10 h-10 rounded-full bg-slate-300">
          <ImgComponent
            src="/images/logo.png"
            alt=""
            class="w-full h-full rounded-full ripple object-cover"
          />
        </div>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import Events from '@/events/events'
import { EVENTS } from '@/shared/constants/commons'
import ImgComponent from '@/presentation/components/img/ImgComponent.vue'
import { CUSTOM_EVENTS } from '@/project/configs/customConstants'
import DataService from '@/features/data/dataService'

const router = useRouter()
const searchQuery = ref('')
const showResults = ref(false)
const searchContainer = ref(null)
const isMd = ref(window.innerWidth >= 768)

const staticMenus = [
  { text: 'Vue d\'ensemble / Statistiques', name: 'dashboard', keywords: ['stat', 'rapport', 'graphe', 'revenu', 'dashboard', 'home', 'accueil', 'overview'] },
  { text: 'Clients', name: 'customers', keywords: ['client', 'user', 'utilisateur', 'acheteur', 'clientele', 'customers'] },
  { text: 'Milestones (Gamification)', name: 'milestones', keywords: ['milestone', 'badge', 'defi', 'gamification', 'palier', 'reseau'] },
  { text: 'Restaurants', name: 'restaurants', keywords: ['resto', 'restaurant', 'boutique', 'commerce', 'partenaire', 'restaurants'] },
  { text: 'Plats', name: 'dishes', keywords: ['plat', 'repas', 'menu', 'nourriture', 'bouffe', 'dish', 'dishes'] },
  { text: 'Catégories', name: 'categories', keywords: ['categorie', 'boisson', 'classement', 'type', 'categories'] },
  { text: 'Accompagnements', name: 'accompaniements', keywords: ['sauce', 'accompagnement', 'ketchup', 'supplement', 'accompaniements'] },
  { text: 'Livreurs (Riders)', name: 'riders', keywords: ['livreur', 'rider', 'coursier', 'livraison', 'moto', 'riders'] },
  { text: 'KYC & Documents', name: 'kyc', keywords: ['kyc', 'document', 'dossier', 'identite', 'validation', 'permis'] },
  { text: 'Dépôts & retraits', name: 'transactions', keywords: ['argent', 'transaction', 'finance', 'historique', 'transactions', 'depot', 'retrait'] },
  { text: 'Retraits (Withdrawals)', name: 'withdrawals', keywords: ['retrait', 'withdrawal', 'finance', 'retrait argent'] },
  { text: 'Commandes (Orders)', name: 'orders', keywords: ['commande', 'achat', 'order', 'repas commande', 'orders'] },
  { text: 'Colis (Packages)', name: 'packages', keywords: ['colis', 'package', 'envoi', 'courrier', 'packages'] },
  { text: 'Frais & Paramètres de livraison', name: 'fees', keywords: ['frais', 'commission', 'livraison tarif', 'parametre', 'cout', 'fees'] },
  { text: 'Promotions / Sponsoring', name: 'promotions', keywords: ['promo', 'sponsoring', 'marketing', 'publicite', 'promotions'] },
  { text: 'Coupons de réduction', name: 'coupons', keywords: ['coupon', 'reduction', 'code promo', 'remise', 'coupons'] },
  { text: 'Bons deals', name: 'deals', keywords: ['deal', 'bon plan', 'promo flash', 'deals'] },
  { text: 'Publicités (Ads)', name: 'ads', keywords: ['pub', 'publicite', 'banniere', 'ads'] },
  { text: 'Notifications de masse', name: 'notification', keywords: ['notif', 'notification', 'push', 'message'] },
  { text: 'Tickets de support', name: 'tickets', keywords: ['support', 'ticket', 'aide', 'reclamation', 'client fache', 'tickets'] },
  { text: 'Boutique Rapidoo (Produits)', name: 'adminProducts', keywords: ['boutique', 'equipement', 'sac', 'produit', 'casque', 'products'] },
  { text: 'Achats d\'équipements', name: 'adminProductPurchases', keywords: ['achat produit', 'commande equipement', 'sac commande', 'purchases'] },
  { text: 'Équipe (Team)', name: 'team', keywords: ['equipe', 'teammate', 'staff', 'admin', 'collaborateur', 'team'] },
  { text: 'Mon Profil / Paramètres', name: 'profile', keywords: ['profil', 'profile', 'mon compte', 'mot de passe'] }
]

function checkResize() {
  isMd.value = window.innerWidth >= 768
}

onMounted(() => {
  window.addEventListener('resize', checkResize)
  document.addEventListener('click', handleOutsideClick)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkResize)
  document.removeEventListener('click', handleOutsideClick)
})

function handleOutsideClick(e) {
  if (searchContainer.value && !searchContainer.value.contains(e.target)) {
    showResults.value = false
  }
}

// Background pre-fetching triggers when user focuses search query
let prefetched = false
function onSearchFocus() {
  showResults.value = true
  if (prefetched) return
  prefetched = true

  // Silently trigger background fetch for main listing keys if not loaded
  const keysToPrefetch = ['customers', 'riders', 'restaurants', 'orders']
  keysToPrefetch.forEach(key => {
    if (!window.cachedData?.[key]) {
      DataService.getSingleData(key, {}).catch(err => {
        console.warn(`Failed background pre-fetch for key: ${key}`, err)
      })
    }
  })
}

function onSearchInput() {
  if (searchQuery.value) {
    showResults.value = true
  }
}

function clearSearch() {
  searchQuery.value = ''
  showResults.value = false
}

// Search Logic
const filteredMenus = computed(() => {
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) return []
  return staticMenus.filter(m => {
    return m.text.toLowerCase().includes(query) || m.keywords.some(k => k.includes(query))
  })
})

const dataResults = computed(() => {
  const query = searchQuery.value.toLowerCase().trim()
  const results = {
    customers: [],
    riders: [],
    restaurants: [],
    orders: []
  }

  if (!query || !window.cachedData) return results

  // Helper: extracts the usable array from a cached entry (handles { result: [] }, plain [], or { data: [] })
  function toArray(cached) {
    if (!cached) return []
    if (Array.isArray(cached)) return cached
    if (Array.isArray(cached.result)) return cached.result
    if (Array.isArray(cached.data)) return cached.data
    return []
  }

  // Search Customers
  const customers = toArray(window.cachedData.customers)
  results.customers = customers.filter(c => {
    return (c.name && c.name.toLowerCase().includes(query)) ||
           (c.phoneNumber && c.phoneNumber.toLowerCase().includes(query)) ||
           (c.email && c.email.toLowerCase().includes(query)) ||
           (c.id && String(c.id).toLowerCase().includes(query))
  }).slice(0, 5)

  // Search Riders
  const riders = toArray(window.cachedData.riders)
  results.riders = riders.filter(r => {
    const fullName = `${r.name || ''} ${r.firstName || ''} ${r.lastName || ''}`.toLowerCase()
    return fullName.includes(query) ||
           (r.phoneNumber && r.phoneNumber.toLowerCase().includes(query)) ||
           (r.email && r.email.toLowerCase().includes(query)) ||
           (r.id && String(r.id).toLowerCase().includes(query))
  }).slice(0, 5)

  // Search Restaurants
  const restaurants = toArray(window.cachedData.restaurants)
  results.restaurants = restaurants.filter(rest => {
    return (rest.name && rest.name.toLowerCase().includes(query)) ||
           (rest.address && rest.address.toLowerCase().includes(query)) ||
           (rest.phone && rest.phone.toLowerCase().includes(query)) ||
           (rest.email && rest.email.toLowerCase().includes(query)) ||
           (rest.id && String(rest.id).toLowerCase().includes(query))
  }).slice(0, 5)

  // Search Orders
  const orders = toArray(window.cachedData.orders)
  results.orders = orders.filter(order => {
    return (order.id && String(order.id).toLowerCase().includes(query)) ||
           (order.customerName && order.customerName.toLowerCase().includes(query)) ||
           (order.storeName && order.storeName.toLowerCase().includes(query)) ||
           (order.reference && order.reference.toLowerCase().includes(query))
  }).slice(0, 5)

  return results
})

const hasDataResults = computed(() => {
  return dataResults.value.customers.length > 0 ||
         dataResults.value.riders.length > 0 ||
         dataResults.value.restaurants.length > 0 ||
         dataResults.value.orders.length > 0
})

function goToMenu(menu) {
  router.push({ name: menu.name })
  clearSearch()
}

function copyAndGo(item, routeName) {
  const id = item.id || ''
  if (id) {
    navigator.clipboard.writeText(id).then(() => {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        type: 'success',
        title: 'ID Copié !',
        body: `L'ID ${id} a été copié dans votre presse-papiers pour recherche facile.`
      })
    }).catch(err => {
      console.error('Failed to copy text: ', err)
    })
  }
  router.push({ name: routeName })
  clearSearch()
}
</script>
